import { ReactNode } from "react";
import { SimpleLink } from "src/components/SimpleLink";
import { AdSlot } from "src/components/Ads/AdSlot";
import { useAuth } from "src/hooks/useAuth";
import { SiteFooter } from "src/components/Layout/SiteFooter";
import { SiteHeader } from "src/components/Layout/SiteHeader";
import { Alert } from "@memento/ui/Alert";
import { ThemeBackground } from "src/components/GroupVideoTheme/ThemeBackground";
import { Container } from "src/components/Elements/Container";
import config from "src/config";
import { cn } from "src/utils/cn";
import { Breakpoint, theme } from "src/styles/theme";
import { VisibleByBreakpoint } from "src/components/VisibleByBreakpoint";
import { HelpBeacon } from "src/components/HelpBeacon";

type ShowAdSlot = boolean | Breakpoint[];

type AdSlotWrapperProps = {
  children: ReactNode;
  showAdSlot?: ShowAdSlot;
};

function AdSlotWrapper({ children, showAdSlot }: AdSlotWrapperProps) {
  if (!showAdSlot) {
    return null;
  } else if (showAdSlot === true) {
    return <div>{children}</div>;
  } else {
    return (
      <VisibleByBreakpoint visibleBreakpoints={showAdSlot}>
        {children}
      </VisibleByBreakpoint>
    );
  }
}

type DefaultLayoutContentProps = {
  children: ReactNode;

  /** override the continueUrl when logging in */
  continueUrl?: string;

  /** page is public (default: false) */
  isPublicPage?: boolean;

  /** If you want to theme the page, include the themeId. Will not do any
   * theming if themeId is empty. */
  themeId?: string | null;

  // put the footer below the page fold, so it isn't visible on initial load
  // (e.g. because it's a distraction)
  nudgeFooter?: boolean;

  /** Which ad slots to show. */
  showAdSlots?: {
    header?: ShowAdSlot;
    footer?: ShowAdSlot;
  };

  variant?: "default" | "gv-renderer"; // gv-renderer is for the group video renderer, which is full-screen with scrolling items within.

  /** Whether to show the footer. */
  showFooter?: boolean;
};

function DefaultLayoutContent({
  children,
  continueUrl,
  isPublicPage = false,
  themeId,
  nudgeFooter = false,
  showFooter = true,
  showAdSlots = {
    header: config.ads.showAdsOnOrganizerPages ? true : isPublicPage,
    footer: config.ads.showAdsOnOrganizerPages ? true : isPublicPage,
  },
  variant = "default",
}: DefaultLayoutContentProps) {
  const { user, isLoggedIn } = useAuth();
  const isThemed = Boolean(themeId);

  return (
    <ThemeBackground themeId={themeId} allowUnthemed>
      <div
        className={cn(
          `min-screen-height relative flex flex-col overflow-hidden`,
          themeId ? null : `bg-gray-50`,
          variant === "gv-renderer" ? "screen-height" : null
        )}
      >
        <div
          className={cn(
            nudgeFooter ? `min-screen-height` : null,
            variant === "default" ? "grow" : null,
            variant === "gv-renderer"
              ? "flex h-full flex-col md:grow md:overflow-hidden"
              : null
          )}
        >
          <AdSlotWrapper showAdSlot={showAdSlots.header}>
            <AdSlot type="leaderboard" adUnit="memento_leaderboard" />
          </AdSlotWrapper>
          <SiteHeader
            themeId={themeId}
            isPublicPage={isPublicPage}
            continueUrl={continueUrl}
            isLoggedIn={isLoggedIn}
          />

          {user && !user.email ? (
            <Alert variant="warning">
              Unfortunately, Facebook did not provide an e-mail address for your
              account. Please <SimpleLink path="/logout">log out</SimpleLink>{" "}
              and sign up with your email address to receive important app
              notifications.
            </Alert>
          ) : null}

          {variant === "gv-renderer" ? (
            children
          ) : (
            <Container className="grow">{children}</Container>
          )}
        </div>

        {showFooter ? (
          <div
            // if this is themed, we want the footer to hug the bottom of the
            // page, otherwise it should sit a bit above the bottom
            className={cn(
              "mt-8 flex-col justify-end",
              isThemed ? undefined : `mb-8`,
              variant === "default" ? "flex" : null,
              variant === "gv-renderer" ? "hidden md:flex" : null
            )}
          >
            <AdSlotWrapper showAdSlot={showAdSlots.footer}>
              <AdSlot type="footerboard" adUnit="memento_footerboard" />
            </AdSlotWrapper>
            <SiteFooter themeId={themeId} />
          </div>
        ) : null}
      </div>
    </ThemeBackground>
  );
}

type DefaultLayoutProps = DefaultLayoutContentProps & {
  disableChat?: boolean;
};

export function DefaultLayout({
  children,
  disableChat = false,
  ...contentProps
}: DefaultLayoutProps) {
  return (
    <>
      <DefaultLayoutContent {...contentProps}>{children}</DefaultLayoutContent>
      <HelpBeacon disableChat={disableChat} color={theme.colors.logo} />
    </>
  );
}
